import React, { useState, useEffect, useCallback } from 'react';
import { getAdminSettings, updateAdminSettings } from '../../services/adminApi';

interface FolderSettings {
  masterSheetParent: string;
  channelSheetsParent: string;
  summaryReportParent: string;
}

interface GamReportQueryConfig {
  cmsMetadataKeyId: number;
  excludedCmsMetadataIds: number[];
  secondaryCmsMetadataKeyId?: number;
  excludedSecondaryCmsMetadataIds?: number[];
  excludedAdvertiserIds: number[];
}

/** How many GAM report CSVs each month's pipeline expects, from a month on. */
interface GamReportUploadSettings {
  expectedPerMonth: number;
  /** "YYYY-MM"; '' = not set (every month expects one report) */
  effectiveFrom: string;
}

interface ScheduleSettings {
  enabled: boolean;
  dayOfMonth: number;
  timeET: string;
}

interface NotificationSettings {
  emailOnComplete: boolean;
  emailOnFailure: boolean;
  notificationEmails: string[];
}

interface AdminSettings {
  adminEmails: string[];
  folders: FolderSettings;
  gamReportQuery: GamReportQueryConfig;
  gamReportUploads: GamReportUploadSettings;
  schedule: ScheduleSettings;
  notifications: NotificationSettings;
  updatedAt?: string;
  updatedBy?: string;
}

const defaultSettings: AdminSettings = {
  adminEmails: [],
  folders: {
    masterSheetParent: '',
    channelSheetsParent: '',
    summaryReportParent: '',
  },
  gamReportQuery: {
    cmsMetadataKeyId: 106689,
    excludedCmsMetadataIds: [],
    excludedAdvertiserIds: [],
  },
  gamReportUploads: {
    expectedPerMonth: 1,
    effectiveFrom: '',
  },
  schedule: {
    enabled: false,
    dayOfMonth: 3,
    timeET: '06:00',
  },
  notifications: {
    emailOnComplete: true,
    emailOnFailure: true,
    notificationEmails: [],
  },
};

const SettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // For adding new items
  const [newAdminEmail, setNewAdminEmail] = useState<string>('');
  const [newNotifEmail, setNewNotifEmail] = useState<string>('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminSettings();
      const s = data?.settings;
      if (!s) {
        setError('No settings found. Please seed defaults first.');
        return;
      }
      setSettings({
        adminEmails: s.adminEmails || [],
        folders: s.folders || defaultSettings.folders,
        gamReportQuery: s.gamReportQuery || defaultSettings.gamReportQuery,
        gamReportUploads: s.gamReportUploads || defaultSettings.gamReportUploads,
        schedule: s.schedule || defaultSettings.schedule,
        notifications: s.notifications || defaultSettings.notifications,
        updatedAt: s.updatedAt,
        updatedBy: s.updatedBy,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    // Unset and untouched stays unset (every month expects one report)
    const gamUploads = settings.gamReportUploads;
    const sendGamUploads = gamUploads.effectiveFrom !== '' || gamUploads.expectedPerMonth !== 1;
    if (sendGamUploads && !/^\d{4}-(0[1-9]|1[0-2])$/.test(gamUploads.effectiveFrom)) {
      setError('GAM Report Uploads: pick the month the expected report count starts from.');
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    setError(null);
    try {
      await updateAdminSettings({
        folders: settings.folders,
        schedule: settings.schedule,
        notifications: settings.notifications,
        adminEmails: settings.adminEmails,
        ...(sendGamUploads ? { gamReportUploads: gamUploads } : {}),
      });
      setSaveMessage('Settings saved successfully.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  // Folder ID helpers
  const updateFolder = (field: keyof FolderSettings, value: string) => {
    setSettings((prev) => ({
      ...prev,
      folders: { ...prev.folders, [field]: value },
    }));
  };

  const updateGamUploads = (field: keyof GamReportUploadSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      gamReportUploads: { ...prev.gamReportUploads, [field]: value },
    }));
  };

  // Schedule helpers
  const updateSchedule = (field: keyof ScheduleSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: value },
    }));
  };

  // Notification helpers
  const updateNotification = (field: keyof NotificationSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, [field]: value },
    }));
  };

  const addNotifEmail = () => {
    const email = newNotifEmail.trim().toLowerCase();
    if (!email) return;
    if (settings.notifications.notificationEmails.includes(email)) return;
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        notificationEmails: [...prev.notifications.notificationEmails, email],
      },
    }));
    setNewNotifEmail('');
  };

  const removeNotifEmail = (email: string) => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        notificationEmails: prev.notifications.notificationEmails.filter((e) => e !== email),
      },
    }));
  };

  // Admin email helpers
  const addAdminEmail = () => {
    const email = newAdminEmail.trim().toLowerCase();
    if (!email) return;
    if (settings.adminEmails.includes(email)) return;
    setSettings((prev) => ({
      ...prev,
      adminEmails: [...prev.adminEmails, email],
    }));
    setNewAdminEmail('');
  };

  const removeAdminEmail = (email: string) => {
    setSettings((prev) => ({
      ...prev,
      adminEmails: prev.adminEmails.filter((e) => e !== email),
    }));
  };

  if (loading) {
    return <p style={styles.loadingText}>Loading settings...</p>;
  }

  return (
    <div>
      {error && <p style={styles.errorText}>{error}</p>}
      {saveMessage && <p style={styles.successText}>{saveMessage}</p>}

      {settings.updatedAt && (
        <p style={styles.metaText}>
          Last updated: {new Date(settings.updatedAt).toLocaleString()}
          {settings.updatedBy && ` by ${settings.updatedBy}`}
        </p>
      )}

      {/* Folder IDs */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Google Drive Folder IDs</h2>
        <div style={styles.fieldsGrid}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Master Sheet Parent Folder</label>
            <input
              style={styles.input}
              value={settings.folders.masterSheetParent}
              onChange={(e) => updateFolder('masterSheetParent', e.target.value)}
              placeholder="Google Drive folder ID"
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Channel Sheets Parent Folder</label>
            <input
              style={styles.input}
              value={settings.folders.channelSheetsParent}
              onChange={(e) => updateFolder('channelSheetsParent', e.target.value)}
              placeholder="Google Drive folder ID"
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Summary Report Parent Folder</label>
            <input
              style={styles.input}
              value={settings.folders.summaryReportParent}
              onChange={(e) => updateFolder('summaryReportParent', e.target.value)}
              placeholder="Google Drive folder ID"
            />
          </div>
        </div>
      </div>

      {/* GAM Report Query (Read-Only) */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>GAM Report Query Configuration</h2>
        <p style={styles.readOnlyNote}>Read-only. Update via backend configuration.</p>
        <div style={styles.fieldsGrid}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>CMS Metadata Key ID</label>
            <div style={styles.readOnlyValue}>{settings.gamReportQuery.cmsMetadataKeyId}</div>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Excluded CMS Metadata IDs</label>
            <div style={styles.readOnlyValue}>
              {settings.gamReportQuery.excludedCmsMetadataIds.length > 0
                ? settings.gamReportQuery.excludedCmsMetadataIds.join(', ')
                : 'None'}
            </div>
          </div>
          {settings.gamReportQuery.secondaryCmsMetadataKeyId && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Secondary CMS Metadata Key ID</label>
              <div style={styles.readOnlyValue}>{settings.gamReportQuery.secondaryCmsMetadataKeyId}</div>
            </div>
          )}
          {settings.gamReportQuery.excludedSecondaryCmsMetadataIds &&
            settings.gamReportQuery.excludedSecondaryCmsMetadataIds.length > 0 && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Excluded Secondary CMS Metadata IDs</label>
              <div style={styles.readOnlyValue}>
                {settings.gamReportQuery.excludedSecondaryCmsMetadataIds.join(', ')}
              </div>
            </div>
          )}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Excluded Advertiser IDs</label>
            <div style={styles.readOnlyValue}>
              {settings.gamReportQuery.excludedAdvertiserIds.length > 0
                ? settings.gamReportQuery.excludedAdvertiserIds.join(', ')
                : 'None'}
            </div>
          </div>
        </div>
      </div>

      {/* GAM Report Uploads */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>GAM Report Uploads</h2>
        <p style={{ ...styles.readOnlyNote, fontStyle: 'normal', lineHeight: 1.5 }}>
          How many GAM report CSVs each month&rsquo;s payout pipeline expects (the main report plus reports for
          channels that need extra filters). They merge into one dataset; the Pipeline tab shows &ldquo;1 of 2&rdquo;
          until every report is uploaded, and processing asks before starting with one missing. Earlier months expect
          one report.
        </p>
        <div style={styles.fieldsGrid}>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="gam-reports-per-month">Reports per month</label>
            <input
              id="gam-reports-per-month"
              style={{ ...styles.input, maxWidth: '120px' }}
              type="number"
              min={1}
              max={5}
              value={settings.gamReportUploads.expectedPerMonth}
              onChange={(e) =>
                updateGamUploads('expectedPerMonth', Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)))
              }
            />
            <span style={styles.hint}>1-5</span>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="gam-reports-from">Starting month</label>
            <input
              id="gam-reports-from"
              style={{ ...styles.input, maxWidth: '180px' }}
              type="month"
              value={settings.gamReportUploads.effectiveFrom}
              onChange={(e) => updateGamUploads('effectiveFrom', e.target.value)}
            />
            <span style={styles.hint}>The first report month the count applies to</span>
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Schedule</h2>
        <div style={styles.fieldsGrid}>
          <div style={styles.fieldGroup}>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.schedule.enabled}
                onChange={(e) => updateSchedule('enabled', e.target.checked)}
                style={styles.checkbox}
              />
              Enabled
            </label>
            <span style={styles.hint}>Automatically run processing on schedule</span>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Day of Month</label>
            <input
              style={{ ...styles.input, maxWidth: '120px' }}
              type="number"
              min={1}
              max={28}
              value={settings.schedule.dayOfMonth}
              onChange={(e) => updateSchedule('dayOfMonth', parseInt(e.target.value) || 1)}
            />
            <span style={styles.hint}>1-28</span>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Time (ET)</label>
            <input
              style={{ ...styles.input, maxWidth: '140px' }}
              type="time"
              value={settings.schedule.timeET}
              onChange={(e) => updateSchedule('timeET', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Notifications</h2>
        <div style={styles.fieldsGrid}>
          <div style={styles.fieldGroup}>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.notifications.emailOnComplete}
                onChange={(e) => updateNotification('emailOnComplete', e.target.checked)}
                style={styles.checkbox}
              />
              Email on Complete
            </label>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.notifications.emailOnFailure}
                onChange={(e) => updateNotification('emailOnFailure', e.target.checked)}
                style={styles.checkbox}
              />
              Email on Failure
            </label>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={styles.label}>Notification Emails</label>
          <div style={styles.emailList}>
            {settings.notifications.notificationEmails.map((email) => (
              <div key={email} style={styles.emailChip}>
                <span>{email}</span>
                <button onClick={() => removeNotifEmail(email)} style={styles.chipRemove}>&times;</button>
              </div>
            ))}
          </div>
          <div style={styles.addEmailRow}>
            <input
              style={{ ...styles.input, flex: 1 }}
              type="email"
              placeholder="Add notification email..."
              value={newNotifEmail}
              onChange={(e) => setNewNotifEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addNotifEmail()}
            />
            <button onClick={addNotifEmail} style={styles.addButton}>Add</button>
          </div>
        </div>
      </div>

      {/* Admin Emails */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Admin Emails</h2>
        <p style={styles.hint}>Users with admin access to Direct Sold processing.</p>
        <div style={styles.emailList}>
          {settings.adminEmails.map((email) => (
            <div key={email} style={styles.emailChip}>
              <span>{email}</span>
              <button onClick={() => removeAdminEmail(email)} style={styles.chipRemove}>&times;</button>
            </div>
          ))}
        </div>
        <div style={styles.addEmailRow}>
          <input
            style={{ ...styles.input, flex: 1 }}
            type="email"
            placeholder="Add admin email..."
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addAdminEmail()}
          />
          <button onClick={addAdminEmail} style={styles.addButton}>Add</button>
        </div>
      </div>

      {/* Save Button */}
      <div style={styles.bottomBar}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ ...styles.primaryButton, ...(saving ? styles.buttonDisabled : {}) }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    padding: '24px',
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--fg-2)',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-app)',
    outline: 'none',
  },
  hint: {
    fontSize: '11px',
    color: 'var(--fg-3)',
    marginTop: '2px',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--fg-1)',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  readOnlyNote: {
    fontSize: '12px',
    color: 'var(--fg-3)',
    fontStyle: 'italic',
    marginBottom: '12px',
    marginTop: '-8px',
  },
  readOnlyValue: {
    padding: '8px 12px',
    background: 'var(--bg-app)',
    borderRadius: '8px',
    border: '1px solid var(--border-1)',
    fontSize: '13px',
    fontFamily: 'var(--font-mono)',
    color: 'var(--fg-2)',
    wordBreak: 'break-all' as const,
  },
  emailList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginBottom: '12px',
    minHeight: '32px',
  },
  emailChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: 'var(--accent-soft)',
    borderRadius: '16px',
    fontSize: '13px',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
  },
  chipRemove: {
    background: 'none',
    border: 'none',
    color: 'var(--fg-3)',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '0 2px',
    lineHeight: 1,
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  addEmailRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    maxWidth: '400px',
  },
  addButton: {
    padding: '8px 16px',
    background: 'var(--accent-soft)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    whiteSpace: 'nowrap' as const,
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 0',
  },
  primaryButton: {
    padding: '10px 24px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    transition: 'background 0.2s',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  loadingText: {
    color: 'var(--fg-2)',
    fontSize: '14px',
  },
  errorText: {
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  successText: {
    color: 'var(--success)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  metaText: {
    fontSize: '12px',
    color: 'var(--fg-3)',
    marginBottom: '16px',
  },
};

export default SettingsTab;
